import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, RefreshCw, X, ChevronDown, ChevronRight } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CREAM = "#F7F5F0";
const NAVY = "#1F3A5F";
const GREEN = "#2E7D5B";
const MUTED = "#6B7280";
const TEXT = "#1F2937";
const BORDER = "#E3E0D9";
const RED = "#B4351B";
const AMBER = "#E58A2E";

const S = {
  page: { minHeight: "100vh", background: CREAM, fontFamily: "'Inter', system-ui, sans-serif", color: TEXT },
  wrap: { maxWidth: 560, margin: "0 auto", padding: "0 16px 48px", paddingTop: 88, boxSizing: "border-box" },
  card: { background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 16, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,.05)", width: "100%", boxSizing: "border-box" },
  input: { width: "100%", height: 46, padding: "0 14px", fontSize: 15, border: `1.5px solid ${BORDER}`, borderRadius: 10, boxSizing: "border-box", background: "#fff", fontFamily: "inherit", color: TEXT },
  btnPrimary: { width: "100%", height: 46, background: NAVY, color: "#fff", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  btnGreen: { width: "100%", height: 50, background: GREEN, color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" },
  err: { background: "#FBEAE5", border: "1px solid #E9BFB1", color: RED, borderRadius: 10, padding: "10px 12px", fontSize: 13, fontWeight: 600, marginTop: 10 },
  lbl: { display: "block", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: MUTED, marginBottom: 4, paddingLeft: 11 },
};

const fmtCash0 = (n) => `$${Math.round(Number(n || 0))}`;
const fmtDay = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "");
const monthLabel = (m) => new Date(`${m}-15T12:00:00`).toLocaleDateString("en-CA", { month: "long", year: "numeric" });

// Long addresses split after the second part: street + floor on line one,
// suite/tower on line two.
function AddressLines({ address }) {
  const parts = (address || "").split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length <= 2) return <>{address}</>;
  return (<>{parts.slice(0, 2).join(", ")}<br />{parts.slice(2).join(", ")}</>);
}

// Compress a chosen/taken photo to a <=1000px JPEG data URL (posters are
// text-heavy, so keep quality reasonably high).
function compressPhoto(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, 1000 / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(img.src);
      resolve(canvas.toDataURL("image/jpeg", 0.72));
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

function StatusPill({ status }) {
  const map = {
    pending: { bg: "rgba(229,138,46,0.10)", fg: AMBER, t: "Pending" },
    approved: { bg: "rgba(46,125,91,0.14)", fg: GREEN, t: "Approved" },
  };
  const s = map[status] || map.pending;
  return (
    // Symmetric bubble: equal padding both sides, with a -8px right margin so
    // the TEXT still right-aligns exactly with the amount and Done above it.
    <span style={{ display: "inline-block", padding: "2px 8px", marginRight: -8, borderRadius: 999, fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".04em", background: s.bg, color: s.fg }}>
      {s.t}
    </span>
  );
}

export default function Marketing() {
  const [token, setToken] = useState(() => sessionStorage.getItem("mkt_token") || "");
  const [code, setCode] = useState("");
  const [enterErr, setEnterErr] = useState("");
  const [entering, setEntering] = useState(false);

  const [name, setName] = useState("");
  const [myCode, setMyCode] = useState("");
  const [sites, setSites] = useState([]);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState(null);
  const [pays, setPays] = useState([]);
  const [paysOpen, setPaysOpen] = useState(false);
  const [month, setMonth] = useState(() => new Date().toLocaleDateString("en-CA").slice(0, 7));
  const [day, setDay] = useState(""); // "" = all days of the selected month
  const [refreshing, setRefreshing] = useState(false);
  const [photoView, setPhotoView] = useState(""); // lightbox url, "" = closed

  const [formOpen, setFormOpen] = useState(false);
  const [fPhoto, setFPhoto] = useState("");
  const [fDate, setFDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [fSite, setFSite] = useState("");
  const [fKind, setFKind] = useState("");
  const [fAmt, setFAmt] = useState("10"); // default $10 per visit — editable
  const [fBusy, setFBusy] = useState(false);
  const [fErr, setFErr] = useState("");

  const H = { headers: { "X-Auth-Token": token } };

  const loadAll = useCallback(async (tok, m) => {
    const h = { headers: { "X-Auth-Token": tok } };
    const home = await axios.get(`${BACKEND}/api/marketing/portal/home`, h);
    setName(home.data.name || ""); setMyCode(home.data.code || "");
    setSites(home.data.sites || []);
    const [sum, list, tp] = await Promise.all([
      axios.get(`${BACKEND}/api/marketing/portal/summary?month=${m}`, h),
      axios.get(`${BACKEND}/api/marketing/portal/submissions?month=${m}`, h),
      axios.get(`${BACKEND}/api/marketing/portal/topups?month=${m}`, h),
    ]);
    setSummary(sum.data); setItems(list.data); setPays(tp.data);
  }, []);

  // After signing in, always land at the very top with the name card fully visible.
  useEffect(() => {
    if (token) window.scrollTo(0, 0);
  }, [token]);

  useEffect(() => {
    if (!token) return;
    loadAll(token, month).catch((e) => {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        sessionStorage.removeItem("mkt_token"); setToken("");
      }
    });
  }, [token, month, loadAll]);

  const enter = async () => {
    if (!code.trim()) return;
    setEntering(true); setEnterErr("");
    try {
      const res = await axios.post(`${BACKEND}/api/marketing/auth/enter`, { code: code.trim() });
      sessionStorage.setItem("mkt_token", res.data.token);
      setToken(res.data.token);
    } catch (e) {
      setEnterErr(e?.response?.data?.detail || "Could not sign in — try again");
    } finally {
      setEntering(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    await loadAll(token, month);
    setRefreshing(false);
  };

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setFPhoto(await compressPhoto(file));
    } catch {
      toast.error("Could not read that photo — try another one");
    }
  };

  const submit = async () => {
    setFErr("");
    if (!fPhoto) { setFErr("A photo of the poster is required"); return; }
    if (!fSite) { setFErr("Choose a site"); return; }
    if (!fKind) { setFErr("Tell us if you put up a new poster or verified the existing one"); return; }
    if (!(Number(fAmt) > 0)) { setFErr("Enter an amount greater than 0"); return; }
    setFBusy(true);
    try {
      await axios.post(`${BACKEND}/api/marketing/portal/submissions`, {
        site_id: fSite, date: fDate, amount: Number(fAmt), kind: fKind, photo_data_url: fPhoto,
      }, H);
      toast.success("Submitted for review");
      setFormOpen(false);
      setFPhoto(""); setFAmt("10"); setFSite(""); setFKind("");
      setMonth(fDate.slice(0, 7));
      await loadAll(token, fDate.slice(0, 7));
    } catch (e) {
      setFErr(e?.response?.data?.detail || "Could not submit — try again");
    } finally {
      setFBusy(false);
    }
  };

  // ---------- entry ----------
  if (!token) {
    return (
      <div style={S.page} data-testid="marketing-entry-page">
        <div style={{ ...S.wrap, maxWidth: 400, paddingTop: 96 }}>
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: NAVY, letterSpacing: -0.5 }}>HepCURE</div>
            <div style={{ fontSize: 13, color: MUTED, marginTop: 4 }}>Marketing — Poster Program</div>
          </div>
          <div style={S.card}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 14, textAlign: "center" }}>Enter your access code</div>
            <input
              data-testid="marketing-code-input"
              style={{ ...S.input, letterSpacing: 2, textTransform: "uppercase", textAlign: "center", fontWeight: 700 }}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && enter()}
              autoCapitalize="characters"
            />
            {enterErr && <div style={S.err} data-testid="marketing-entry-error">{enterErr}</div>}
            <button
              data-testid="marketing-enter-button"
              style={{ ...S.btnPrimary, marginTop: 14, opacity: entering ? 0.7 : 1 }}
              onClick={enter}
              disabled={entering}
            >
              {entering ? "Checking…" : "Open marketing"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const months = [];
  const nowM = new Date().toLocaleDateString("en-CA").slice(0, 7);
  for (let i = -2; i < 12; i++) {
    const d = new Date(); d.setDate(15); d.setMonth(d.getMonth() - i);
    months.push(d.toISOString().slice(0, 7));
  }
  if (!months.includes(nowM)) months.unshift(nowM);

  const selectedSite = sites.find((s) => s.id === fSite) || null;

  // Scheduled visits come from the admin's monthly plan — the user never
  // adds sites; they complete the visits listed here.
  const todayYMD = new Date().toISOString().slice(0, 10);
  const doneIds = new Set((items || []).map((s) => s.site_id));
  // Completed visits drop OFF the scheduled list (and its day options) — it
  // only shows what's left to do; finished work lives in Submissions below.
  const schedTotal = sites.filter((s) => (s.scheduled || {})[month]).length;
  const pendingVisits = sites
    .map((s) => ({ ...s, visit: (s.scheduled || {})[month] || "" }))
    .filter((s) => s.visit && !doneIds.has(s.id));
  const schedDates = [...new Set(pendingVisits.map((s) => s.visit))].sort();
  const schedRows = pendingVisits
    .filter((s) => !day || s.visit === day)
    .sort((a, b) => a.visit.localeCompare(b.visit));
  // The day filter slices the Scheduled list only — Submissions stays month-wide.

  // ---------- portal ----------
  return (
    <div style={S.page} data-testid="marketing-portal-page">
      <div style={S.wrap}>
        <div style={{ ...S.card, marginBottom: 14, padding: "16px 18px", display: "flex", alignItems: "flex-start", gap: 10 }} data-testid="marketing-header">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: MUTED, textTransform: "uppercase", letterSpacing: ".08em" }}>Marketing</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: NAVY, marginTop: 2, lineHeight: 1.2 }} data-testid="marketing-name">{name || "Poster Program"}</div>
          </div>
          <button
            data-testid="marketing-refresh"
            onClick={refresh}
            disabled={refreshing}
            style={{ height: 34, padding: "0 14px", fontSize: 12, fontWeight: 700, color: NAVY, background: "#fff", borderRadius: 9, border: `1px solid ${NAVY}`, cursor: refreshing ? "default" : "pointer", opacity: refreshing ? 0.55 : 1, fontFamily: "inherit", flexShrink: 0, marginTop: 2, display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <RefreshCw style={{ width: 12, height: 12 }} strokeWidth={2.5} />
            {refreshing ? "…" : "Refresh"}
          </button>
        </div>

        {summary && (
          <div data-testid="marketing-balance" style={{ ...S.card, marginBottom: 14, padding: "10px 12px", display: "flex", gap: 6, flexWrap: "nowrap" }}>
            {[
              ["Approved", summary.approved_total, NAVY, "approved"],
              ["In review", summary.in_review_total, NAVY, "in-review"],
              ["Paid", summary.paid_total, NAVY, "paid"],
              ["Owing", summary.owing, summary.owing < 0 ? RED : GREEN, "owing"],
            ].map(([k, v, color, slug]) => (
              <div key={k} style={{ flex: 1, minWidth: 0, textAlign: "center" }}>
                <div style={{ fontSize: 9, fontWeight: 800, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap" }}>{k}</div>
                <div style={{ fontSize: 14, fontWeight: 800, color, marginTop: 2, whiteSpace: "nowrap" }} data-testid={`marketing-bal-${slug}`}>{fmtCash0(v)}</div>
              </div>
            ))}
          </div>
        )}

        {/* Payments — collapsed by default; expands to the person's payment
            history for the selected month (one row per top-up). */}
        <div data-testid="marketing-payments" style={{ ...S.card, padding: "12px 20px", marginBottom: 14 }}>
          <button data-testid="marketing-payments-toggle" onClick={() => setPaysOpen(!paysOpen)}
            style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit" }}>
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: MUTED, textAlign: "left" }}>
              Payments {pays.length > 0 && `(${pays.length})`}
            </span>
            {paysOpen ? <ChevronDown style={{ width: 14, height: 14, color: MUTED, flexShrink: 0 }} /> : <ChevronRight style={{ width: 14, height: 14, color: MUTED, flexShrink: 0 }} />}
            <span style={{ flex: 1 }} />
            {pays.length > 0 && (
              <span style={{ fontSize: 12, fontWeight: 800, color: GREEN, textAlign: "right" }}>{fmtCash0(pays.reduce((a, p) => a + p.amount, 0))}</span>
            )}
          </button>
          {paysOpen && (
            <div style={{ marginTop: 8 }} data-testid="marketing-payments-list">
              {pays.length === 0 ? (
                <div style={{ fontSize: 12, color: MUTED, textAlign: "center", padding: "6px 0" }} data-testid="marketing-payments-empty">No payments for {monthLabel(month)} yet.</div>
              ) : (
                pays.map((p) => (
                  <div key={p.id} data-testid={`marketing-payment-${p.id}`}
                    style={{ display: "flex", alignItems: "baseline", gap: 8, padding: "6px 0", borderTop: `1px solid ${BORDER}` }}>
                    <span style={{ flex: 1, fontSize: 12.5, fontWeight: 600, color: TEXT }}>{fmtDay(p.date)}{p.note ? ` · ${p.note}` : ""}</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: GREEN }}>{fmtCash0(p.amount)}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {!formOpen && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "4px 2px 10px" }}>
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: MUTED, flex: 1 }}>Scheduled</span>
              <select data-testid="marketing-month" value={month} onChange={(e) => { setMonth(e.target.value); setDay(""); }} style={{ height: 30, border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: 12, fontFamily: "inherit", padding: "0 8px", color: TEXT, background: "#fff" }}>
                {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
              </select>
              <select data-testid="marketing-day" value={day} onChange={(e) => setDay(e.target.value)} style={{ height: 30, border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: 12, fontFamily: "inherit", padding: "0 8px", color: TEXT, background: "#fff" }}>
                <option value="">All days</option>
                {schedDates.map((d) => <option key={d} value={d}>{fmtDay(d)}</option>)}
              </select>
            </div>

            {schedRows.length === 0 ? (
              <div style={{ ...S.card, textAlign: "center", color: MUTED, fontSize: 13, marginBottom: 14 }} data-testid="marketing-sched-empty">
                {sites.length === 0
                  ? "No poster locations assigned yet — HepCURE will schedule visits for you."
                  : day ? `Nothing scheduled on ${fmtDay(day)}.`
                  : schedTotal > 0 ? `All scheduled visits are completed for ${monthLabel(month)} — see Submissions below.`
                  : `No visits scheduled for ${monthLabel(month)}.`}
              </div>
            ) : (
              schedRows.map((s) => (
                <div key={s.id} data-testid={`marketing-sched-${s.id}`} style={{ ...S.card, padding: "10px 12px", marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 12.5, fontWeight: 700, color: TEXT }}>{s.name}</span>
                        {s.location_type && (
                          <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 999, textTransform: "uppercase", letterSpacing: ".04em", background: "#F1EFE9", color: NAVY, flexShrink: 0 }}>{s.location_type}</span>
                        )}
                      </div>
                      <div style={{ fontSize: 10.5, color: MUTED, marginTop: 1 }}><AddressLines address={s.address} />{s.postal_code ? ` · ${s.postal_code}` : ""}</div>
                    </div>
                    <span style={{ fontSize: 9, fontWeight: 800, color: s.visit < todayYMD ? RED : AMBER, textTransform: "uppercase", letterSpacing: ".04em", flexShrink: 0 }}>
                      {s.visit < todayYMD ? "Overdue" : "Due"}
                    </span>
                    <button data-testid={`marketing-sched-submit-${s.id}`} onClick={() => { setFSite(s.id); setFormOpen(true); }}
                      style={{ height: 26, padding: "0 10px", background: GREEN, color: "#fff", border: "none", borderRadius: 7, fontSize: 10.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>Submit</button>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {formOpen && (
          <div style={{ ...S.card, marginBottom: 14 }} data-testid="marketing-form">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: TEXT }}>New submission</div>
              <button data-testid="marketing-form-close" onClick={() => setFormOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: MUTED, padding: 4 }}>
                <X style={{ width: 16, height: 16 }} strokeWidth={2.5} />
              </button>
            </div>
            <div style={{ fontSize: 11.5, color: MUTED, marginBottom: 6 }}>Photo of the poster up at the location</div>

            <span style={S.lbl}>Poster photo *</span>
            <label data-testid="marketing-photo-drop" style={{ display: "block", border: `1.5px dashed ${NAVY}`, borderRadius: 12, padding: fPhoto ? 8 : "20px 10px", textAlign: "center", background: "#FAF9F6", cursor: "pointer" }}>
              <input type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={pickPhoto} data-testid="marketing-photo-input" />
              {fPhoto ? (
                <img src={fPhoto} alt="Poster" style={{ width: "100%", borderRadius: 8 }} />
              ) : (
                <>
                  <ImagePlus style={{ width: 24, height: 24, color: NAVY, margin: "0 auto" }} strokeWidth={2} />
                  <div style={{ fontSize: 12, fontWeight: 700, color: NAVY, marginTop: 4 }}>Take photo / upload</div>
                  <div style={{ fontSize: 10, color: MUTED, marginTop: 2 }}>Poster must be visible up at the location</div>
                </>
              )}
            </label>

            <span style={S.lbl}>Date *</span>
            <input data-testid="marketing-date" type="date" style={{ ...S.input, height: 42, fontSize: 14, textAlign: "left", letterSpacing: 0 }} value={fDate} onChange={(e) => setFDate(e.target.value)} />

            <span style={S.lbl}>This visit *</span>
            <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
              {[
                ["new", "New poster put up"],
                ["verify", "Verified still up"],
              ].map(([k, t]) => (
                <button key={k} type="button" data-testid={`marketing-kind-${k}`} onClick={() => setFKind(k)}
                  style={{
                    flex: 1, minHeight: 44, padding: "6px 8px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit",
                    fontSize: 12.5, fontWeight: 700, lineHeight: 1.25,
                    border: fKind === k ? `2px solid ${NAVY}` : `1.5px solid ${BORDER}`,
                    background: fKind === k ? NAVY : "#fff",
                    color: fKind === k ? "#fff" : TEXT,
                  }}>
                  {t}
                </button>
              ))}
            </div>

            <span style={S.lbl}>Site</span>
            {selectedSite && (
              <div data-testid="marketing-site-details" style={{ background: "#FAF9F6", border: `1px solid ${BORDER}`, borderRadius: 10, padding: "10px 12px", marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: TEXT }}>{selectedSite.name}</span>
                  {selectedSite.location_type && (
                    <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 999, textTransform: "uppercase", letterSpacing: ".04em", background: "#fff", border: `1px solid ${BORDER}`, color: NAVY, flexShrink: 0 }}>{selectedSite.location_type}</span>
                  )}
                </div>
                <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }} data-testid="marketing-site-address">
                  <AddressLines address={selectedSite.address || "—"} />
                </div>
                <div style={{ fontSize: 11.5, color: MUTED, marginTop: 1 }} data-testid="marketing-site-cityline">
                  {[selectedSite.region, selectedSite.province].filter(Boolean).join(", ")}{selectedSite.postal_code ? ` ${selectedSite.postal_code}` : ""}
                </div>
                {selectedSite.contact && (
                  <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }} data-testid="marketing-site-contact">Contact: {selectedSite.contact}</div>
                )}
                {selectedSite.phone && (
                  <a data-testid="marketing-site-phone" href={`tel:+1${selectedSite.phone.replace(/\D/g, "").replace(/^1(\d{10})$/, "$1")}`}
                    style={{ display: "inline-block", fontSize: 12, fontWeight: 700, color: GREEN, textDecoration: "none", marginTop: 4 }}>
                    {selectedSite.phone}
                  </a>
                )}
              </div>
            )}

            <span style={S.lbl}>Amount *</span>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: 14, top: 12, fontSize: 14, fontWeight: 700, color: MUTED }}>$</span>
              <input data-testid="marketing-amount" inputMode="decimal" style={{ ...S.input, height: 42, fontSize: 14, textAlign: "left", letterSpacing: 0, paddingLeft: 28 }} placeholder="10.00" value={fAmt} onChange={(e) => setFAmt(e.target.value)} />
            </div>

            {fErr && <div style={S.err} data-testid="marketing-form-error">{fErr}</div>}

            <button data-testid="marketing-submit" style={{ ...S.btnGreen, marginTop: 16, opacity: fBusy ? 0.7 : 1 }} onClick={submit} disabled={fBusy}>
              {fBusy ? "Submitting…" : "Submit for review"}
            </button>
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "14px 2px 10px" }}>
          <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: MUTED }}>Submissions</span>
        </div>

        {items === null ? (
          <div style={{ textAlign: "center", color: MUTED, fontSize: 13, padding: 20 }}>Loading…</div>
        ) : items.length === 0 ? (
          <div style={{ ...S.card, textAlign: "center", color: MUTED, fontSize: 13 }} data-testid="marketing-empty">No submissions this month.</div>
        ) : (
          items.map((s) => (
            <div key={s.id} data-testid={`marketing-sub-${s.id}`} style={{ ...S.card, padding: "10px 12px", display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
              {s.photo_url ? (
                <button onClick={() => setPhotoView(`${BACKEND}${s.photo_url}`)} data-testid={`marketing-photo-view-${s.id}`}
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer", flexShrink: 0 }}>
                  <img src={`${BACKEND}${s.photo_url}`} alt="Poster" style={{ width: 46, height: 46, borderRadius: 8, objectFit: "cover", display: "block" }} />
                </button>
              ) : (
                <div style={{ width: 46, height: 46, borderRadius: 8, background: "#D8D5CE" }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: TEXT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.address}</div>
                <div style={{ fontSize: 10.5, color: MUTED, marginTop: 1 }}>{s.site} · {s.location_type}</div>
                <div style={{ fontSize: 10.5, color: MUTED, marginTop: 1 }}>{fmtDay(s.date)}</div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: TEXT }}>{fmtCash0(s.amount)}</div>
                <div style={{ marginTop: 3, display: "flex", justifyContent: "flex-end" }}>
                  <StatusPill status={s.status} />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {photoView && (
        <div data-testid="marketing-lightbox" onClick={() => setPhotoView("")}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,32,0.92)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <button data-testid="marketing-lightbox-close" onClick={() => setPhotoView("")}
            style={{ position: "absolute", top: 14, right: 14, background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 999, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff" }}>
            <X style={{ width: 18, height: 18 }} />
          </button>
          <img src={photoView} alt="Poster" onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 12, objectFit: "contain" }} />
        </div>
      )}
    </div>
  );
}
